import { type MutableRefObject, useRef, useState } from "react";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useLoadNewNurses, useLoadScheduleRecurringShifts, useLoadScheduleShifts, useUpdateDepartmentFirstSunday, useUpdateExistingNurses } from "../../hooks";
import type { DataLoadFile } from "./consts";
import { isPending } from "./dataLoadOrder";
import { errorMessage, payloadFor } from "./dataLoadPayload";

type Params = {
	departmentId: string | undefined;
	orderedFiles: DataLoadFile[];
	/** Live file list, read per iteration so the chain never acts on a row that has since changed. */
	filesRef: MutableRefObject<DataLoadFile[]>;
	updateFile: (fileId: string, partial: Partial<DataLoadFile>) => void;
	allowDuplicates: boolean;
	minStartDate: string;
};

/** Phase D of a run: the ordered, strictly sequential loads, with the week-parity interruption. */
export function useDataLoadUpload({ departmentId, orderedFiles, filesRef, updateFile, allowDuplicates, minStartDate }: Params) {
	const { chosenDepartment, setChosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	const [isLoading, setIsLoading] = useState(false);
	const [showWeekParityDialog, setShowWeekParityDialog] = useState(false);
	/**
	 * A ref, not state: the chain resumes inside the same async function the dialog interrupted,
	 * before a `setState` would have flushed. As state this reads stale and the dialog reopens.
	 */
	const weekParityAnswered = useRef(false);

	const { mutateAsync: loadNewNurses } = useLoadNewNurses();
	const { mutateAsync: updateExistingNurses } = useUpdateExistingNurses();
	const { mutateAsync: loadScheduleShifts } = useLoadScheduleShifts();
	const { mutateAsync: loadRecurringShifts } = useLoadScheduleRecurringShifts();
	const { mutateAsync: updateFirstSunday, isPending: isUpdatingFirstSunday } = useUpdateDepartmentFirstSunday();

	const callLoaderFor = (entry: DataLoadFile, csvFile: File) => {
		const base = { departmentId: departmentId as string, file: csvFile, ...(entry.promptHistoryId ? { promptHistoryId: entry.promptHistoryId } : {}) };
		if (entry.kind === "roster") return entry.rosterAction === "update" ? updateExistingNurses(base) : loadNewNurses(base);
		if (entry.kind === "shifts") return loadScheduleShifts({ ...base, allowDuplicateNurseMatches: allowDuplicates });
		return loadRecurringShifts({ ...base, allowDuplicateNurseMatches: allowDuplicates, minStartDate: minStartDate || undefined });
	};

	const loadOne = async (entry: DataLoadFile): Promise<boolean> => {
		if (!departmentId) return false;
		// Cleared up front so a retry does not show the previous attempt's error next to a fresh result.
		updateFile(entry.id, { loadError: null });
		try {
			const result = await callLoaderFor(entry, payloadFor(entry));
			updateFile(entry.id, { loadResult: result as DataLoadFile["loadResult"] });
			track("data_load_file_success", { department_id: departmentId, kind: entry.kind, mode: entry.mode, roster_action: entry.rosterAction });
			return true;
		} catch (error) {
			// The file keeps its place in the queue so it is still the next one to load and can be
			// retried; without a recorded error it would be indistinguishable from one never attempted.
			updateFile(entry.id, { loadError: errorMessage(error) });
			trackError("data_load_file_error", { department_id: departmentId, kind: entry.kind, mode: entry.mode, error_message: errorMessage(error) });
			return false;
		}
	};

	/**
	 * Week parity is a property of the DEPARTMENT, so it is confirmed once per run. Gated on `kind`,
	 * never on mode: a template uploaded as-is still reaches `loadScheduleRecurringShifts` and still
	 * carries the question. Fires before the first template that will ACTUALLY load.
	 */
	const needsWeekParity = (entry: DataLoadFile) => entry.kind === "template" && Boolean(chosenDepartment?.first_sunday) && !weekParityAnswered.current;

	/**
	 * Load every pending file, one at a time, in load order. Stops on the first failure.
	 *
	 * Strictly sequential, never `Promise.all`: each load endpoint reads the department's current
	 * state to de-duplicate (`process_nurses_file` snapshots existing nurses, `insert_shifts` reads
	 * the scheduled shifts in the file's range). Run concurrently, two roster files both see the
	 * pre-load state and a person listed in both is created twice.
	 */
	const runLoads = async (queue: DataLoadFile[]) => {
		if (!departmentId) return;
		setIsLoading(true);
		try {
			let loaded = 0;
			for (const queued of queue) {
				// Re-read the file rather than trusting the queued snapshot: it was captured before the
				// first await, and a row removed or skipped since then must not still be loaded.
				const entry = filesRef.current.find((file) => file.id === queued.id);
				if (!entry || !isPending(entry)) continue;
				if (needsWeekParity(entry)) {
					setShowWeekParityDialog(true);
					return;
				}
				if (!(await loadOne(entry))) {
					trackError("data_load_batch_error", { department_id: departmentId, loaded_count: loaded, file_count: queue.length });
					showSnackbar(`Stopped after ${loaded} file(s) — fix the error and load the rest`, "error");
					return;
				}
				loaded += 1;
			}
			track("data_load_batch_success", { department_id: departmentId, loaded_count: loaded });
		} finally {
			setIsLoading(false);
		}
	};

	const handleSwitchAndResume = async () => {
		if (!chosenDepartment?.department_id || !chosenDepartment.first_sunday) return;
		const newFirstSunday = chosenDepartment.first_sunday.plus({ weeks: 1 });
		try {
			await updateFirstSunday({ departmentId: chosenDepartment.department_id, firstSunday: newFirstSunday.toFormat("yyyy-MM-dd") });
			setChosenDepartment((prev) => ({ ...prev, first_sunday: newFirstSunday }));
			weekParityAnswered.current = true;
			setShowWeekParityDialog(false);
			await runLoads(orderedFiles);
		} catch {
			// Error handled by the hook's onError -> showErrorSnackbar
		}
	};

	const handleKeepAsIsAndResume = async () => {
		weekParityAnswered.current = true;
		setShowWeekParityDialog(false);
		await runLoads(orderedFiles);
	};

	const resetRun = () => {
		weekParityAnswered.current = false;
		setShowWeekParityDialog(false);
	};

	return {
		isLoading,
		uploadAll: () => runLoads(orderedFiles),
		uploadOne: (entry: DataLoadFile) => runLoads([entry]),
		showWeekParityDialog,
		setShowWeekParityDialog,
		handleSwitchAndResume,
		handleKeepAsIsAndResume,
		isUpdatingFirstSunday,
		chosenDepartment,
		resetRun,
	};
}
